/**
 * Spawn eggs: one per mob type the game has. Creative-only items (no recipe),
 * stack 64. Icons are the vanilla per-mob `item/<mob>_spawn_egg.png` sprites
 * (1.21.5+ dropped the tinted base+overlay pair); the two colours below are
 * the classic vanilla egg colours, kept for procedural fallbacks / UI accents.
 *
 * Use (main.ts): right-click a block face spawns the mob in the cell beside
 * that face; one egg is used up outside creative. Right-clicking a mob of the
 * same type would spawn a baby — this game has no baby mobs yet, so it does
 * nothing.
 */

import type { MobType } from './types';
import { I } from './itemIds';

export interface SpawnEggDef { mob: MobType; base: number; spots: number }

export const SPAWN_EGGS: ReadonlyMap<number, SpawnEggDef> = new Map<number, SpawnEggDef>([
  [I.ZOMBIE_SPAWN_EGG, { mob: 'zombie', base: 0x00afaf, spots: 0x799c65 }],
  [I.SKELETON_SPAWN_EGG, { mob: 'skeleton', base: 0xc1c1c1, spots: 0x494949 }],
  [I.CREEPER_SPAWN_EGG, { mob: 'creeper', base: 0x0da70b, spots: 0x000000 }],
  [I.SPIDER_SPAWN_EGG, { mob: 'spider', base: 0x342d27, spots: 0xa80e0e }],
  [I.COW_SPAWN_EGG, { mob: 'cow', base: 0x443626, spots: 0xa1a1a1 }],
  [I.PIG_SPAWN_EGG, { mob: 'pig', base: 0xf0a5a2, spots: 0xdb635f }],
  [I.SHEEP_SPAWN_EGG, { mob: 'sheep', base: 0xe7e7e7, spots: 0xffb5b5 }],
  [I.CHICKEN_SPAWN_EGG, { mob: 'chicken', base: 0xa1a1a1, spots: 0xff0000 }],
  [I.ZOMBIFIED_PIGLIN_SPAWN_EGG, { mob: 'zombified_piglin', base: 0xea9393, spots: 0x4c7129 }],
  [I.PIGLIN_SPAWN_EGG, { mob: 'piglin', base: 0x995f40, spots: 0xf9f3a4 }],
  [I.MAGMA_CUBE_SPAWN_EGG, { mob: 'magma_cube', base: 0x340000, spots: 0xfcfc00 }],
  [I.GHAST_SPAWN_EGG, { mob: 'ghast', base: 0xf9f9f9, spots: 0xbcbcbc }],
  [I.BLAZE_SPAWN_EGG, { mob: 'blaze', base: 0xf6b201, spots: 0xfff87e }],
  [I.WITHER_SKELETON_SPAWN_EGG, { mob: 'wither_skeleton', base: 0x141414, spots: 0x474d4d }],
  [I.HOGLIN_SPAWN_EGG, { mob: 'hoglin', base: 0xc66e55, spots: 0x5f6464 }],
  [I.STRIDER_SPAWN_EGG, { mob: 'strider', base: 0x9c3436, spots: 0x4d494d }],
  [I.ENDERMAN_SPAWN_EGG, { mob: 'enderman', base: 0x161616, spots: 0x000000 }],
  [I.VILLAGER_SPAWN_EGG, { mob: 'villager', base: 0x563c33, spots: 0xbd8b72 }],
  [I.PILLAGER_SPAWN_EGG, { mob: 'pillager', base: 0x532f36, spots: 0x959b9b }],
  [I.ZOMBIE_VILLAGER_SPAWN_EGG, { mob: 'zombie_villager', base: 0x563c33, spots: 0x799c65 }],
  [I.VINDICATOR_SPAWN_EGG, { mob: 'vindicator', base: 0x959b9b, spots: 0x275e61 }],
  [I.SLIME_SPAWN_EGG, { mob: 'slime', base: 0x51a03e, spots: 0x7ebf6e }],
  [I.CAVE_SPIDER_SPAWN_EGG, { mob: 'cave_spider', base: 0x0c424e, spots: 0xa80e0e }],
  [I.HUSK_SPAWN_EGG, { mob: 'husk', base: 0x797061, spots: 0xe6cc94 }],
  [I.STRAY_SPAWN_EGG, { mob: 'stray', base: 0x617677, spots: 0xddeaea }],
  [I.DROWNED_SPAWN_EGG, { mob: 'drowned', base: 0x8ff1d7, spots: 0x799c65 }],
  [I.SILVERFISH_SPAWN_EGG, { mob: 'silverfish', base: 0x6e6e6e, spots: 0x303030 }],
  [I.ENDERMITE_SPAWN_EGG, { mob: 'endermite', base: 0x161616, spots: 0x6e6e6e }],
  [I.PHANTOM_SPAWN_EGG, { mob: 'phantom', base: 0x43518a, spots: 0x88ff00 }],
  [I.CAT_SPAWN_EGG, { mob: 'cat', base: 0xefc88e, spots: 0x957650 }],
  [I.SQUID_SPAWN_EGG, { mob: 'squid', base: 0x223b4d, spots: 0x708899 }],
  [I.COD_SPAWN_EGG, { mob: 'cod', base: 0xc1a76a, spots: 0xe5c797 }],
  [I.SALMON_SPAWN_EGG, { mob: 'salmon', base: 0xa00f0f, spots: 0x0e8359 }],
  [I.BAT_SPAWN_EGG, { mob: 'bat', base: 0x4c3e30, spots: 0x0f0f0f }],
  [I.WITCH_SPAWN_EGG, { mob: 'witch', base: 0x340000, spots: 0x51a03e }],
  [I.WOLF_SPAWN_EGG, { mob: 'wolf', base: 0xd7d3d3, spots: 0xceaf96 }],
  [I.PANDA_SPAWN_EGG, { mob: 'panda', base: 0xe7e7e7, spots: 0x1b1b22 }],
  [I.FOX_SPAWN_EGG, { mob: 'fox', base: 0xd5b69f, spots: 0xcc6920 }],
  [I.PARROT_SPAWN_EGG, { mob: 'parrot', base: 0x0da70b, spots: 0xff0000 }],
  [I.WITHER_SPAWN_EGG, { mob: 'wither', base: 0x141414, spots: 0x4b5a5a }], // creative-only /summon equivalent
  [I.WARDEN_SPAWN_EGG, { mob: 'warden', base: 0x0f4649, spots: 0x39d6e0 }],
  [I.HORSE_SPAWN_EGG, { mob: 'horse', base: 0xc09e7d, spots: 0xeee500 }],
  [I.DONKEY_SPAWN_EGG, { mob: 'donkey', base: 0x534237, spots: 0x867566 }],
  [I.WANDERING_TRADER_SPAWN_EGG, { mob: 'wandering_trader', base: 0x456296, spots: 0xeaa430 }],
  [I.LLAMA_SPAWN_EGG, { mob: 'llama', base: 0xc09e7d, spots: 0x995f40 }],
  [I.TRADER_LLAMA_SPAWN_EGG, { mob: 'trader_llama', base: 0xea8924, spots: 0x445692 }],
  [I.RABBIT_SPAWN_EGG, { mob: 'rabbit', base: 0x995f40, spots: 0x734831 }],
  [I.GOAT_SPAWN_EGG, { mob: 'goat', base: 0xa5947c, spots: 0x55493e }],
  [I.TURTLE_SPAWN_EGG, { mob: 'turtle', base: 0xe7e7e7, spots: 0x00afaf }],
  [I.MOOSHROOM_SPAWN_EGG, { mob: 'mooshroom', base: 0xa00f10, spots: 0xb7b7b7 }],
  [I.POLAR_BEAR_SPAWN_EGG, { mob: 'polar_bear', base: 0xeeeede, spots: 0xd5d6cd }],
  [I.OCELOT_SPAWN_EGG, { mob: 'ocelot', base: 0xefde7d, spots: 0x564434 }],
  [I.SNOW_GOLEM_SPAWN_EGG, { mob: 'snow_golem', base: 0xd9f2f2, spots: 0x81a4a4 }],
  [I.AXOLOTL_SPAWN_EGG, { mob: 'axolotl', base: 0xfbc1e3, spots: 0xa62d74 }],
  [I.DOLPHIN_SPAWN_EGG, { mob: 'dolphin', base: 0x223b4d, spots: 0xf9f9f9 }],
  [I.GUARDIAN_SPAWN_EGG, { mob: 'guardian', base: 0x5a8272, spots: 0xf17d30 }],
  [I.ELDER_GUARDIAN_SPAWN_EGG, { mob: 'elder_guardian', base: 0xceccba, spots: 0x747693 }],
  [I.SHULKER_SPAWN_EGG, { mob: 'shulker', base: 0x946794, spots: 0x4d3852 }],
  [I.EVOKER_SPAWN_EGG, { mob: 'evoker', base: 0x959b9b, spots: 0x1e1c1a }],
  [I.VEX_SPAWN_EGG, { mob: 'vex', base: 0x7a90a4, spots: 0xe8edf1 }],
  [I.RAVAGER_SPAWN_EGG, { mob: 'ravager', base: 0x757470, spots: 0x5b5049 }],
  [I.ZOGLIN_SPAWN_EGG, { mob: 'zoglin', base: 0xc66e55, spots: 0xe6e6e6 }],
  [I.PIGLIN_BRUTE_SPAWN_EGG, { mob: 'piglin_brute', base: 0x592a10, spots: 0xf9f3a4 }],
]);

export const isSpawnEgg = (id: number): boolean => SPAWN_EGGS.has(id);
export const spawnEggMob = (id: number): MobType | undefined => SPAWN_EGGS.get(id)?.mob;

/**
 * Where an egg used on block (bx, by, bz)'s face (nx, ny, nz) puts the mob's
 * feet: the centre of the neighbouring cell (vanilla: the relative position,
 * mob placed at its bottom centre).
 */
export function eggSpawnPos(bx: number, by: number, bz: number, nx: number, ny: number, nz: number): [number, number, number] {
  return [bx + nx + 0.5, by + ny, bz + nz + 0.5];
}
